import React from 'react';
import styled from 'styled-components/macro';
import { Receipt } from '../../../../types/receipts';

import {
    getAggregatedReceiptsByMonth,
    getAvgDaysBetweenPurchase,
    getGeneratedRevenue,
    getNumTransactions,
    getReceiptsByCurrency,
    getReceiptsCurrencies
} from '../../../customerHelpers';
import NoTransactions from './NoTransactions';
import { SectionHeader } from '../../../common';
import RevenueSummary from './RevenueSummary';
import RevenueChart from './RevenueChart';
import { WithTranslation, withTranslation } from 'react-i18next';

interface ChartsProps extends WithTranslation {
    receipts: Receipt[];
    isLoading: boolean;
}

const Charts = (
    {
        receipts,
        isLoading,
        t,
    }: ChartsProps
) => {

    if (isLoading) return null;

    const currencies = getReceiptsCurrencies(receipts);
    const hasMultipleCurrencies = currencies.length > 1;
    const hasNoTransactions = currencies.length === 0;

    if (hasNoTransactions) return <NoTransactions/>;

    return (
        <div>
            {currencies.map(currency => {
                const receiptsByCurrency = getReceiptsByCurrency(currency, receipts);
                const chartData = getAggregatedReceiptsByMonth(receiptsByCurrency);
                const revenue = getGeneratedRevenue(receiptsByCurrency);
                const numTransactions = getNumTransactions(receiptsByCurrency);
                const avgTransactionCost = (revenue / numTransactions) || 0;
                const sumTransactions = chartData.reduce((acc, elem) => elem.total + acc, 0);
                const avgDaysBetweenPurchase = getAvgDaysBetweenPurchase(receiptsByCurrency);

                return (
                    <KPISection key={currency}>
                        <SectionHeader>
                            {t('customers.customer.customer_kpi', { amount: numTransactions })} {hasMultipleCurrencies && `(${currency})`}
                        </SectionHeader>
                        <RevenueSummary
                            generatedRevenue={revenue}
                            avgTransactionCost={avgTransactionCost}
                            avgDaysBetweenPurchase={avgDaysBetweenPurchase}
                            currency={currency}
                        />
                        {sumTransactions > 0 && <RevenueChart data={chartData} currency={currency}/>}
                    </KPISection>
                );
            })}
        </div>
    );
};

const KPISection = styled.div`
    & + & {
        margin-top: 30px;
    }
`;

export default withTranslation()(Charts);
